import { useEffect, useState } from 'react';
import { useApolloClient } from '@apollo/client';

import { GET_HISTORY } from 'store/queries';
import { useManagedQuery } from 'utils/managedHooks';

import { getScheduleChanges, getSegmentsData } from './utils';

/**
 * @typedef {Object} UseScheduleChangedQueryData
 * @property {JourneySegment} journeySegment - the journey segment data
 * @property {HistoryDetails} scheduleChange - the schedule change data
 */

/**
 * @typedef {Object} UseScheduleChangedQueryResponse
 * @property {UseScheduleChangedQueryData|null} data - the journey segment and schedule change data
 * @property {boolean} loading - whether the query is loading
 */

/**
 * useScheduleChangedQuery
 * - Get history data
 * - Filter history data for schedule changes
 * - Get the journey segments from the schedule changes
 * - Get the journey segment data for matching segmentId
 * - Return the journey segment data and schedule change data for the match
 *
 * @param {Object} options
 * @param {string} options.bookingReference - the booking reference
 * @param {string} options.segmentId - the segment ID
 * @returns {UseScheduleChangedQueryResponse}
 */
const useScheduleChangedQuery = ({ bookingReference, segmentId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [segmentDataCalled, setSegmentDataCalled] = useState(false);
  const [segmentDataLoading, setSegmentDataLoading] = useState(false);

  const client = useApolloClient();

  const { data: historyData, loading: historyLoading } = useManagedQuery(GET_HISTORY, { variables: { bookingReference } });

  // Data
  useEffect(() => {
    if (!historyData) {
      return;
    }

    if (segmentDataCalled) {
      return;
    }

    const getSegmentDataFromHistory = async () => {
      setSegmentDataCalled(true);
      setSegmentDataLoading(true);
      const {
        history: { details }
      } = historyData;
      const scheduleChanges = getScheduleChanges(details);

      if (!scheduleChanges) {
        setSegmentDataLoading(false);
        return;
      }

      const journeySegmentIds = Array.from(scheduleChanges.keys());
      const journeySegments = await getSegmentsData({ bookingReference, client, journeySegmentIds });
      const journeySegment = journeySegments.get(segmentId);

      if (!journeySegment) {
        setSegmentDataLoading(false);
        return;
      }

      const { id: journeySegmentId } = journeySegment;
      const scheduleChange = scheduleChanges.get(journeySegmentId);

      if (!scheduleChange) {
        setSegmentDataLoading(false);
        return;
      }

      const segmentData = { journeySegment, scheduleChange };
      setData(segmentData);

      setSegmentDataLoading(false);
    };

    getSegmentDataFromHistory();
  }, [segmentDataCalled, historyData, bookingReference, client, segmentId]);

  // Loading
  useEffect(() => {
    const shouldBeLoading = historyLoading || segmentDataLoading;
    if (loading !== shouldBeLoading) {
      setLoading(shouldBeLoading);
    }
  }, [historyLoading, loading, segmentDataLoading, setLoading]);

  return { data, loading };
};

export default useScheduleChangedQuery;
